import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { formatApiErrorDetail } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Shield, Users, FileText, Star, DollarSign, Check, X, Loader2, ArrowLeft, MoreVertical, Send } from 'lucide-react';

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [pendingTranscribers, setPendingTranscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [openUserMenu, setOpenUserMenu] = useState(null);
  const [privateMessage, setPrivateMessage] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [docsRes, usersRes, ratingsRes, pendingRes] = await Promise.all([
        api.get('/admin/documents', { withCredentials: true }),
        api.get('/admin/users', { withCredentials: true }),
        api.get('/admin/ratings', { withCredentials: true }),
        api.get('/admin/pending-transcribers', { withCredentials: true }),
      ]);
      setDocuments(docsRes.data);
      setUsers(usersRes.data);
      setRatings(ratingsRes.data);
      setPendingTranscribers(pendingRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.info('Administrator access is required.');
        navigate('/', { replace: true });
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleTranscriberAction = async (userId, action) => {
    setActionLoading(prev => ({ ...prev, [userId]: true }));
    try {
      await api.post('/admin/approve-transcriber', { user_id: userId, action });
      toast.success(action === 'approve' ? 'Transcriber approved!' : 'Application rejected');
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handlePayout = async (docId, action) => {
    setActionLoading(prev => ({ ...prev, [docId]: true }));
    try {
      await api.post('/admin/payout', { document_id: docId, action });
      toast.success(action === 'approve' ? 'Payout approved!' : 'Payout rejected');
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionLoading(prev => ({ ...prev, [docId]: false }));
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/', { replace: true });
      return;
    }
    fetchAll();
  }, [user, navigate, fetchAll]);

  const changeRole = async (target, role) => {
    setActionLoading(prev => ({ ...prev, [target._id]: true }));
    try {
      await api.post(`/admin/users/${target._id}/role`, { role });
      toast.success(`${target.name}'s rank is now ${role}`);
      setOpenUserMenu(null); await fetchAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setActionLoading(prev => ({ ...prev, [target._id]: false })); }
  };

  const sendPrivateMessage = async (target) => {
    if (!privateMessage.trim()) return;
    setActionLoading(prev => ({ ...prev, [target._id]: true }));
    try {
      await api.post('/admin/messages', { user_id: target._id, text: privateMessage });
      toast.success(`Private message sent to ${target.name}`);
      setPrivateMessage(''); setOpenUserMenu(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setActionLoading(prev => ({ ...prev, [target._id]: false })); }
  };

  const completedDocs = documents.filter(d => (d.status === 'done' || d.status === 'completed') && d.payout_status === 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 size={32} className="animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="pb-nav" data-testid="admin-page">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/account')}
          className="flex items-center gap-2 text-sm text-[var(--brand-text-sec)] hover:text-[var(--brand-text)] mb-4 transition-colors"
        >
          <ArrowLeft size={18} />
          Back to Account
        </button>

        <div className="flex items-center gap-3 mb-6">
          <Shield size={24} className="text-[var(--brand-primary)]" />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--brand-text)]">
            Admin Dashboard
          </h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total Users" value={users.length} icon={Users} />
          <StatCard label="Documents" value={documents.length} icon={FileText} />
          <StatCard label="Ratings" value={ratings.length} icon={Star} />
          <StatCard label="Pending Payouts" value={completedDocs.length} icon={DollarSign} />
        </div>

        <Tabs defaultValue="payouts">
          <TabsList className="w-full border-b border-[var(--brand-border)] bg-transparent p-0 rounded-none h-auto mb-6 flex-wrap">
            <TabsTrigger value="payouts" data-testid="admin-tab-payouts" className="px-4 py-3 text-sm font-medium text-[var(--brand-text-sec)] data-[state=active]:text-[var(--brand-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-primary)] rounded-none data-[state=active]:shadow-none data-[state=active]:bg-transparent">
              Payouts
            </TabsTrigger>
            <TabsTrigger value="transcribers" data-testid="admin-tab-transcribers" className="px-4 py-3 text-sm font-medium text-[var(--brand-text-sec)] data-[state=active]:text-[var(--brand-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-primary)] rounded-none data-[state=active]:shadow-none data-[state=active]:bg-transparent">
              Transcribers
              {pendingTranscribers.length > 0 && (
                <Badge className="ml-2 bg-[var(--brand-danger)]/10 text-[var(--brand-danger)] border-none rounded-sm text-[10px]">{pendingTranscribers.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="ratings" data-testid="admin-tab-ratings" className="px-4 py-3 text-sm font-medium text-[var(--brand-text-sec)] data-[state=active]:text-[var(--brand-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-primary)] rounded-none data-[state=active]:shadow-none data-[state=active]:bg-transparent">
              Ratings
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="px-4 py-3 text-sm font-medium text-[var(--brand-text-sec)] data-[state=active]:text-[var(--brand-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-primary)] rounded-none data-[state=active]:shadow-none data-[state=active]:bg-transparent">
              Current Users
            </TabsTrigger>
          </TabsList>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            {completedDocs.length === 0 ? (
              <EmptyState message="No pending payouts" />
            ) : (
              <div className="space-y-3">
                {completedDocs.map((doc) => (
                  <div key={doc.id} className="bg-white border border-[var(--brand-border)] p-4 flex items-center justify-between" data-testid={`payout-${doc.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-[var(--brand-text)] truncate">{doc.title}</p>
                      <p className="text-xs text-[var(--brand-text-sec)]">
                        Transcriber: {doc.transcriber_name} | Price: <span className="font-mono">${doc.offered_price?.toFixed(2)}</span>
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        data-testid={`approve-payout-${doc.id}`}
                        onClick={() => handlePayout(doc.id, 'approve')}
                        disabled={actionLoading[doc.id]}
                        className="w-8 h-8 bg-[var(--brand-accent)] text-white flex items-center justify-center hover:bg-[var(--brand-accent)]/80 transition-colors disabled:opacity-50"
                      >
                        {actionLoading[doc.id] ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      </button>
                      <button
                        data-testid={`reject-payout-${doc.id}`}
                        onClick={() => handlePayout(doc.id, 'reject')}
                        disabled={actionLoading[doc.id]}
                        className="w-8 h-8 bg-[var(--brand-danger)] text-white flex items-center justify-center hover:bg-[var(--brand-danger)]/80 transition-colors disabled:opacity-50"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Transcribers Tab */}
          <TabsContent value="transcribers">
            {pendingTranscribers.length === 0 ? (
              <EmptyState message="No pending transcriber applications" />
            ) : (
              <div className="space-y-3">
                {pendingTranscribers.map((u) => (
                  <div key={u._id} className="bg-white border border-[var(--brand-border)] p-4 flex items-center justify-between" data-testid={`transcriber-${u._id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-[var(--brand-text)]">{u.name}</p>
                      <p className="text-xs text-[var(--brand-text-sec)]">{u.email}</p>
                      <p className="text-xs text-[var(--brand-text-sec)] mt-1">
                        WPM: <span className="font-mono font-bold">{u.typing_test_wpm}</span> | 
                        Accuracy: <span className="font-mono font-bold">{u.typing_test_accuracy}%</span>
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        data-testid={`approve-transcriber-${u._id}`}
                        onClick={() => handleTranscriberAction(u._id, 'approve')}
                        disabled={actionLoading[u._id]}
                        className="px-3 py-1.5 bg-[var(--brand-accent)] text-white text-xs font-medium hover:bg-[var(--brand-accent)]/80 transition-colors disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        data-testid={`reject-transcriber-${u._id}`}
                        onClick={() => handleTranscriberAction(u._id, 'reject')}
                        disabled={actionLoading[u._id]}
                        className="px-3 py-1.5 bg-[var(--brand-danger)] text-white text-xs font-medium hover:bg-[var(--brand-danger)]/80 transition-colors disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Ratings Tab */}
          <TabsContent value="ratings">
            {ratings.length === 0 ? (
              <EmptyState message="No ratings yet" />
            ) : (
              <div className="space-y-3">
                {ratings.map((r) => (
                  <div key={r.id} className="bg-white border border-[var(--brand-border)] p-4" data-testid={`rating-${r.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-[var(--brand-text-sec)]">Document: {r.document_id?.slice(0, 8)}...</span>
                      <span className="text-xl font-black font-mono text-[var(--brand-warning)]">{r.rating}/10</span>
                    </div>
                    {r.comment && <p className="text-sm text-[var(--brand-text)]">{r.comment}</p>}
                    <p className="text-xs text-[var(--brand-text-sec)] mt-1">
                      Transcriber: {r.transcriber_id?.slice(0, 8)}...
                    </p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u._id} className="relative bg-white border border-[var(--brand-border)] p-4 flex items-start justify-between" data-testid={`user-${u._id}`}>
                  <div className="flex gap-3 flex-1 min-w-0">
                    <span className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${u.is_online ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,.15)]' : 'bg-slate-300'}`} title={u.is_online ? 'Online now' : 'Offline'} />
                    <div><p className="font-semibold text-sm text-[var(--brand-text)]">{u.name}</p><p className="text-xs text-[var(--brand-text-sec)]">{u.email}</p><p className="text-[10px] mt-1 text-[var(--brand-text-sec)]">{u.is_online ? 'Online now' : u.last_seen_at ? `Last seen ${new Date(u.last_seen_at).toLocaleString()}` : 'Not seen yet'}</p></div>
                  </div>
                  <div className="flex items-center gap-2"><Badge className={`rounded-sm text-[10px] ${
                    u.role === 'admin' ? 'bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]' :
                    u.role === 'transcriber' ? 'bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]' :
                    'bg-[var(--brand-border)] text-[var(--brand-text-sec)]'
                  }`}>
                    {u.role}
                  </Badge><button aria-label={`Manage ${u.name}`} onClick={() => { setOpenUserMenu(openUserMenu === u._id ? null : u._id); setPrivateMessage(''); }} className="p-1 hover:bg-[var(--brand-bg)]"><MoreVertical size={18} /></button></div>
                  {openUserMenu === u._id && <div className="absolute right-3 top-12 z-20 w-72 bg-white border border-[var(--brand-border)] shadow-xl p-3"><p className="text-xs font-bold uppercase tracking-wider mb-2">Change rank</p><div className="grid grid-cols-3 gap-1 mb-3">{['user','transcriber','admin'].map(role => <button key={role} disabled={actionLoading[u._id] || u.role === role} onClick={() => changeRole(u, role)} className="border px-2 py-1.5 text-xs capitalize disabled:bg-[var(--brand-bg)] disabled:opacity-60">{role}</button>)}</div><p className="text-xs font-bold uppercase tracking-wider mb-2">Private message</p><textarea value={privateMessage} onChange={(event) => setPrivateMessage(event.target.value)} maxLength={2000} rows={3} placeholder="Write a message…" className="w-full border p-2 text-sm resize-none" /><button disabled={!privateMessage.trim() || actionLoading[u._id]} onClick={() => sendPrivateMessage(u)} className="mt-2 w-full bg-[var(--brand-primary)] text-white py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-50"><Send size={14} /> Send as Admin</button></div>}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[var(--brand-border)] p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} className="text-[var(--brand-text-sec)]" />
        <span className="text-xs text-[var(--brand-text-sec)] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-black font-mono text-[var(--brand-text)]">{value}</p>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="text-center py-12">
      <p className="text-[var(--brand-text-sec)]">{message}</p>
    </div>
  );
}
